import {Evaluable, evaluateWhenFunction} from '../evaluable';
import {Values} from "../common-types/common-types";
import {IObservable} from "../observable/observable";
import {
    asyncOperationCreateIdleState,
    asyncOperationCreatePendingState,
    asyncOperationCreateRejectedState,
    IAsyncOperationState
} from "../async-state";
import {EventEmitter, EventReference} from "../event-emitter";
import {assertDefined} from "../assertions";
import {AsyncTasksScheduler} from "../async-tasks-scheduler/async-tasks-scheduler";
import {CachedValueWithParams} from "../cached-value/cached-value";
import {asyncForEach} from "../async-utils/async-utils";

const SkipUpdateToken = Symbol('SkipUpdateToken');

type FetchFunctionResult<T> = typeof SkipUpdateToken | T;

const AsyncCollectionConcurrentFetchingCommonActions = {
    SerializeAfterPendingRequestCompletion: 'SerializeAfterPendingRequestCompletion',
    SkipAndWaitForPendingRequestCompletion: 'SkipAndWaitForPendingRequestCompletion',
} as const;

export const AsyncCollectionConcurrentFetchingAction = {
    serializeAfterPendingRequestCompletion<FETCH_PARAM> (adjustParam?: FETCH_PARAM) {
        if (adjustParam !== undefined) {
            return {
                adjustParam: adjustParam
            }
        } else {
            return AsyncCollectionConcurrentFetchingCommonActions.SerializeAfterPendingRequestCompletion
        }
    },
    skipAndWaitForPendingRequestCompletion () {
        return AsyncCollectionConcurrentFetchingCommonActions.SkipAndWaitForPendingRequestCompletion
    },
};



export interface IAsyncCollection<FETCH_PARAM> extends IObservable<AsyncCollection.Events> {
    eventFetchOperationStateChanged: EventReference<AsyncCollection.Events, 'eventFetchOperationStateChanged'>;
    eventValueChanged: EventReference<AsyncCollection.Events, 'eventValueChanged'>;
    eventReset: EventReference<AsyncCollection.Events, 'eventReset'>;

    getFetchOperationAsyncState () : IAsyncOperationState;

    triggerFetch (param?: FETCH_PARAM) : Promise<unknown>;

    reset () : void;
}

export namespace AsyncCollection {

    export type ConcurrentFetchingAction = Values<typeof AsyncCollectionConcurrentFetchingCommonActions>;

    export type ConcurrentFetchingActionResolver<FETCH_PARAM> =
        (fetchParam?: FETCH_PARAM, pendingFetchParam?: FETCH_PARAM) => (
            ConcurrentFetchingAction |
            {adjustParam: FETCH_PARAM}
        )

    export interface Events extends IObservable.Events {
        eventFetchOperationStateChanged: void;
        eventValueChanged: void;
        eventReset: void;
    }

    export interface FetchContext<T, FETCH_PARAM> {
        currentValue: T;
        fetchParam?: FETCH_PARAM;
        skip: () => typeof SkipUpdateToken;
    }

    export interface ConstructorOptions<T, FETCH_PARAM = unknown> {
        initialValue: T;
        fetchFunction: (fetchContext: FetchContext<T, FETCH_PARAM>) => Promise<FetchFunctionResult<T>>;
        onUpdated?: (updatedValue: T) => void;
        onFetchOperationStateChange?: (fetchState: IAsyncOperationState) => void;
        concurrentFetchingAction?: Evaluable<ConcurrentFetchingActionResolver<FETCH_PARAM>>
    }
}

export class AsyncCollection<T, FETCH_PARAM = unknown> implements IAsyncCollection<FETCH_PARAM> {

    private initialValue: T;
    private eventEmitter = new EventEmitter<AsyncCollection.Events>();
    protected currentValue: T;
    private fetchFunction: AsyncCollection.ConstructorOptions<T, FETCH_PARAM>['fetchFunction'];
    private onUpdated?: AsyncCollection.ConstructorOptions<T, FETCH_PARAM>['onUpdated'];
    private onFetchOperationStateChange?: AsyncCollection.ConstructorOptions<T, FETCH_PARAM>['onFetchOperationStateChange'];
    private concurrentFetchingAction: Required<AsyncCollection.ConstructorOptions<T, FETCH_PARAM>>['concurrentFetchingAction'];

    private currentFetchOperation?: Promise<FetchFunctionResult<T>>;
    private currentFetchOperationParam?: FETCH_PARAM;
    private lastFetchOperationAsyncState: IAsyncOperationState;

    private readonly refreshScheduler = new AsyncTasksScheduler<() => Promise<void>>({
        executeTask: task => task(),
        handleTaskScheduling: (task, controller) => {
            controller.clearPendingTasks();
            controller.enqueueTask(task);
        }
    });

    private currentRefreshOperation?: Promise<void>;
    private shouldRetryRefreshAfterFetch = false;

    constructor (options: AsyncCollection.ConstructorOptions<T, FETCH_PARAM>) {
        this.currentValue = this.initialValue = options.initialValue;
        this.fetchFunction = options.fetchFunction;
        this.onUpdated = options.onUpdated;
        this.onFetchOperationStateChange = options.onFetchOperationStateChange;
        this.lastFetchOperationAsyncState = asyncOperationCreateIdleState();
        this.concurrentFetchingAction = options.concurrentFetchingAction ??
            AsyncCollectionConcurrentFetchingCommonActions.SkipAndWaitForPendingRequestCompletion;
    }

    public get eventUpdated () {
        return this.eventEmitter.createEventReference('eventUpdated');
    }

    public get eventValueChanged () {
        return this.eventEmitter.createEventReference('eventValueChanged');
    }

    public get eventFetchOperationStateChanged () {
        return this.eventEmitter.createEventReference('eventFetchOperationStateChanged');
    }

    public get eventReset () {
        return this.eventEmitter.createEventReference('eventReset');
    }

    public getValue () {
        return this.currentValue;
    }

    public getFetchOperationAsyncState () {
        return this.lastFetchOperationAsyncState;
    }

    public reset () {
        this.handleUpdate(this.initialValue);

        this.eventEmitter.emit('eventReset');
    }

    public hasActiveConsumers(): boolean {
        return this.eventEmitter.hasListeners('eventValueChanged') ||
               this.eventEmitter.hasListeners('eventReset');
    }

    /**
     * Protected method for updating state during refresh operations.
     * Only applies the update if no newer refresh is pending (prevents stale updates).
     * Also applies processValueAfterFetch for consistency with regular fetch operations.
     * @param refreshOperation The refresh operation promise to check if still current
     * @param updatedValue The new value to apply
     */
    protected handleUpdateFromRefresh(refreshOperation: Promise<void>, updatedValue: T): void {
        // Check if this refresh is still current (not cancelled by a fetch)
        // AND check if no newer refresh is pending
        if (this.currentRefreshOperation === refreshOperation && !this.refreshScheduler.hasPendingTasks()) {
            const processedValue = this.processValueAfterFetch(updatedValue);
            this.handleUpdate(processedValue);
        }
    }

    /**
     * Returns whether this collection supports refresh operations.
     * Subclasses should override to return true if they implement refreshInternal().
     */
    public supportsRefresh(): boolean {
        return false;
    }

    /**
     * Protected method for subclasses to implement refresh logic.
     * Should re-fetch data without clearing the current state.
     * Use handleUpdateFromRefresh() to apply updates with stale prevention.
     * @param _refreshOperation The refresh operation promise to check if still current
     */
    protected async refreshInternal(_refreshOperation: Promise<void>): Promise<void> {
        // No-op by default - subclasses should override
    }

    /**
     * Public refresh method that delegates to refreshInternal().
     * Throws if refresh is not supported.
     * If a fetch is in progress, waits for it to complete before refreshing.
     */
    public async refresh(): Promise<void> {
        if (!this.supportsRefresh()) {
            throw new Error('This AsyncCollection does not support refresh. Override supportsRefresh() and refreshInternal() to enable.');
        }

        // Wait for any active fetch to complete first
        if (this.currentFetchOperation) {
            await this.currentFetchOperation.catch(() => {
                // Ignore fetch errors, we're refreshing anyway
            });
        }

        // Create a placeholder promise that we'll resolve later
        // This ensures the promise identity is established before the async task references it
        let resolveRefreshOperation!: () => void;
        let rejectRefreshOperation!: (error: any) => void;
        
        const refreshOperation = new Promise<void>((resolve, reject) => {
            resolveRefreshOperation = resolve;
            rejectRefreshOperation = reject;
        });
        
        this.currentRefreshOperation = refreshOperation;

        // Now execute the refresh logic
        (async () => {
            try {
                this.refreshScheduler.scheduleTask(async () => {
                    await this.refreshInternal(refreshOperation);
                });
                await this.refreshScheduler.waitForAllTasksToComplete();
                resolveRefreshOperation();
            } catch (error) {
                rejectRefreshOperation(error);
            }
        })();

        try {
            await refreshOperation;
        } finally {
            // Clear if we're still current
            if (this.currentRefreshOperation === refreshOperation) {
                this.currentRefreshOperation = undefined;
            }
        }
    }

    /**
     * Protected method that subclasses can override to indicate if refresh is safe.
     * Base class returns true (assumes safe by default).
     * List-based collections should override to check for getItemId availability.
     */
    protected canSafelyRefresh(): boolean {
        return true;
    }

    /**
     * Smart data invalidation that chooses between refresh() and reset() based on whether
     * there are active consumers (e.g., mounted components) and if refresh is safe.
     * 
     * - **If consumers are listening AND refresh is supported AND safe**: Calls `refresh()` for smooth UI update
     * - **Otherwise**: Calls `reset()` to clear state
     */
    public async refreshIfHasConsumersElseReset(): Promise<void> {
        if (this.hasActiveConsumers() && this.supportsRefresh() && this.canSafelyRefresh()) {
            await this.refresh();
        } else {
            this.reset();
        }
    }

    public async triggerFetch (param?: FETCH_PARAM) : Promise<T> {
        const currentFetchOperation = this.currentFetchOperation;

        // If refresh is active, cancel it and mark for retry
        if (this.currentRefreshOperation) {
            this.currentRefreshOperation = undefined; // Cancel by clearing
            this.shouldRetryRefreshAfterFetch = true; // Mark for retry after fetch completes
        }

        if (currentFetchOperation) {

            const concurrentFetchingAction = evaluateWhenFunction(this.concurrentFetchingAction, param, this.currentFetchOperationParam);

            switch (concurrentFetchingAction) {
                case AsyncCollectionConcurrentFetchingCommonActions.SkipAndWaitForPendingRequestCompletion:
                    await currentFetchOperation;
                    return this.currentValue;
                case AsyncCollectionConcurrentFetchingCommonActions.SerializeAfterPendingRequestCompletion:
                    await currentFetchOperation;
                    return this.triggerFetch(param);
                default:
                    await currentFetchOperation;
                    return this.triggerFetch(concurrentFetchingAction.adjustParam);
            }
        } else {
            const fetchOperation = this.currentFetchOperation =
                this.fetchFunction({
                    currentValue: this.currentValue,
                    skip: () => SkipUpdateToken,
                    fetchParam: param
                });

            this.currentFetchOperationParam = param;

            this.updateAsyncState(asyncOperationCreatePendingState(fetchOperation));

            let fetchResult: FetchFunctionResult<T> | undefined = undefined;
            let isStillCurrent = false;

            try {
                fetchResult = await fetchOperation;
            } catch (error) {
                this.updateAsyncState(asyncOperationCreateRejectedState(error));
                throw error;
            } finally {
                // Capture if we're still current BEFORE clearing
                isStillCurrent = this.currentFetchOperation === fetchOperation;
                
                // Only clear if we're still current
                if (isStillCurrent) {
                    this.currentFetchOperation = undefined;
                    this.currentFetchOperationParam = undefined;

                    // If we cancelled a refresh, restart it now
                    if (this.shouldRetryRefreshAfterFetch) {
                        this.shouldRetryRefreshAfterFetch = false;
                        // Fire and forget - don't block the fetch
                        this.refresh().catch(() => {
                            // Refresh failures are non-fatal
                        });
                    }
                }
            }

            // Check if we were superseded by another fetch
            if (!isStillCurrent) {
                // We were superseded - return current value without updating
                return this.currentValue;
            }

            this.eventEmitter.batchEventEmitting('eventUpdated', () => {
                this.eventEmitter.batchEventEmitting('eventValueChanged', () => {
                    this.eventEmitter.batchEventEmitting('eventFetchOperationStateChanged', () => {
                        if (fetchResult !== SkipUpdateToken) {
                            const processedResult = this.processValueAfterFetch(assertDefined(fetchResult));
                            this.handleUpdate(processedResult);
                        }

                        this.updateAsyncState(asyncOperationCreateIdleState());
                    })
                })
            })

            return this.currentValue;
        }
    }

    protected handleUpdate (updatedValue: T) {
        this.currentValue = updatedValue;

        this.onUpdated?.(updatedValue);
        this.eventEmitter.emit('eventValueChanged');
        this.eventEmitter.emit('eventUpdated');
    }

    /**
     * Protected method for post-processing values after fetch.
     * Subclasses can override to transform the fetched value (e.g., deduplication).
     * Base implementation returns the value unchanged (identity).
     * @param value The value returned from the fetch function
     * @returns The processed value to be stored
     */
    protected processValueAfterFetch(value: T): T {
        return value;
    }

    private updateAsyncState (asyncState: IAsyncOperationState) {
        this.lastFetchOperationAsyncState = asyncState;

        this.onFetchOperationStateChange?.(asyncState);
        this.eventEmitter.emit('eventFetchOperationStateChanged');
        this.eventEmitter.emit('eventUpdated');
    }
}


export async function cachedAsyncCollectionsRefreshIfHasConsumersElseReset<T extends AsyncCollection<any, any>, P extends object> (
    cachedValueWithParams: CachedValueWithParams<T, P>,
    shouldReset: (params: NoInfer<P>) => boolean
) {
    const asyncCollectionsToRefresh: T[] = [];

    cachedValueWithParams.iterateEntries((asyncCollection, params) => {
        if (shouldReset(params)) {
            asyncCollectionsToRefresh.push(asyncCollection);
        }
    });

    await asyncForEach(asyncCollectionsToRefresh, asyncCollection => asyncCollection.refreshIfHasConsumersElseReset())
}

export function cachedAsyncCollectionsReset<T extends AsyncCollection<any, any>, P extends object> (
    cachedValueWithParams: CachedValueWithParams<T, P>,
    shouldReset: (params: NoInfer<P>) => boolean
) {
    cachedValueWithParams.iterateEntries((asyncCollection, params) => {
        if (shouldReset(params)) {
            asyncCollection.reset();
        }
    })
}